'use client';

import { useEffect, useRef } from 'react';
import { isBrowser } from 'utils';
import { createPayPalOrderAction } from '../../actions/paypal-express/createPayPalOrderAction';
import { payPalApproveOrderResponse } from 'services/api/client';
import { trackPayPalExpressClick } from '../../analytics/overview';

interface UsePayPalButtonsProps {
  style: Record<string, unknown>;
  language: string;
  country: string;
  onApproveResponse: (response: Response) => Promise<void>;
  onGeneralError: () => void;
}

/**
 * Mounts the PayPal SDK buttons into a ref div on component mount.
 *
 * The useEffect renders once (empty deps) — re-rendering would destroy and
 * recreate the button, breaking the PayPal SDK state machine.
 * Callbacks are stable references passed from PayPalSmartButton.
 */
export function usePayPalButtons({
  style,
  language,
  country,
  onApproveResponse,
  onGeneralError,
}: UsePayPalButtonsProps) {
  const paypalRef = useRef<HTMLDivElement>(null);

  // Stored per button mount — shared across createOrder / onApprove closures
  const payPalExpressDataRef = useRef({
    merchantId: '',
    payId: '',
    paypalOrderId: '',
    paypalUrl: '',
    transId: '',
  });

  useEffect(() => {
    // Guard against React 18 Strict Mode double-invoking effects:
    // Strict Mode mounts → unmounts (no cleanup) → remounts, causing render() to fire twice.
    if (!isBrowser() || !window.paypal || !paypalRef.current || paypalRef.current.children.length > 0) {
      return;
    }

    window.paypal
      .Buttons({
        style,
        // Called by the PayPal SDK when the user clicks the button, before the PayPal
        // overlay opens. Initialises the order server-side (payment.express.init) and
        // returns the PayPal order ID that the SDK uses to open the checkout overlay.
        createOrder: async () => {
          trackPayPalExpressClick();

          const response = await createPayPalOrderAction();

          if (!response?.merchantId) {
            onGeneralError();

            return;
          }

          payPalExpressDataRef.current = response;

          return response.paypalOrderId;
        },
        // Called by the PayPal SDK after the user confirms payment inside the overlay.
        // Sends the approved order to the payment service, then hands the response
        // to the caller (PayPalSmartButton) which decides whether to register the
        // user or redirect to login.
        onApprove: async (data: { orderID: string }) => {
          const { transId } = payPalExpressDataRef.current;

          if (!transId || !data.orderID) {
            return;
          }

          const approveResponse = await payPalApproveOrderResponse({
            transactionId: transId,
            paypalOrderId: data.orderID,
            acceptLanguageHeader: `${language}-${country}`,
          });

          await onApproveResponse(approveResponse);
        },
      })
      .render(paypalRef.current);
    // Empty deps: PayPal SDK renders once on mount — re-rendering breaks the button.
    // Callbacks are stable references from PayPalSmartButton.
  }, []);

  return paypalRef;
}
